<script setup lang="ts">
import { optionalValueFallbackFormatter } from '@/helpers/ep/formatters'
import type { IFragmentFilters } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES } from '@/types/shared/pagination'
import type { IContainerMiner } from '@/types/rc/containers'
import { getContainerMiners } from '@/services/rc.service'
import { Icon } from '@iconify/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { computed, h, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import MoveContainerMinersDialog from '../containers/MoveContainerMinersDialog.vue'
import type { IContainer } from '@/types/rc/shipments'
import { moveMinersBetweenContainers, removeMinersFromContainer } from '@/services/rc/containers.service'
import { useRouter } from 'vue-router'

const queryClient = useQueryClient()
const router = useRouter()

const isMoveContainerDialogOpen = ref<boolean>(false)
const props = defineProps<{ container: IContainer; loading?: boolean }>()

const searchQuery = ref<string>()
const filters = reactive<IFragmentFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  fragment: undefined,
})

const minerCount = ref<number>()

const { isPending, isFetching, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['container-miners', props.container.id, filters],
  queryFn: () =>
    getContainerMiners(props.container.id, filters).then((data) => {
      minerCount.value = data?.total_records
      filters.page = data.page
      return data
    }),
  placeholderData: (prev: any) => prev,
})

const handleMinersRemove = useMutation({
  mutationFn: ({ containerID, minerIDs }: { containerID: number; minerIDs: number[] }) =>
    removeMinersFromContainer(containerID, minerIDs),
  onMutate: (variables) => {
    return { params: variables }
  },
  onSuccess: (_data, variables) => {
    ElMessage({
      message: `Removed ${variables.minerIDs.length} miners successfully`,
      type: 'success',
    })
    selectedMiners.value = []
    queryClient.invalidateQueries({ queryKey: ['container-miners'] })
  },
  onError: (e) => {
    ElMessage.error({
      message: e.message,
    })
  },
})

const handleMinersMove = useMutation({
  mutationFn: ({ origin, destination, minerIDs }: { origin: number; destination: number; minerIDs: number[] }) =>
    moveMinersBetweenContainers(origin, destination, minerIDs),
  onMutate: (variables) => {
    return { params: variables }
  },
  onSuccess: (_data, variables) => {
    ElMessage({
      message: `${variables.minerIDs.length} miners moved successfully`,
      type: 'success',
    })
    selectedMiners.value = []
    queryClient.invalidateQueries({ queryKey: ['container-miners'] })
  },
  onError: (e) => {
    ElMessage.error({
      message: e.message,
    })
  },
})

async function RemoveMinersConfirmationModal(
  title: string,
  message: string,
  containerID: number,
  minerIDs: number[]
): Promise<any> {
  await ElMessageBox({
    title,
    message,
    showCancelButton: true,
    confirmButtonText: 'Remove',
    confirmButtonClass: 'el-button--danger',
    beforeClose: async (action, instance, done) => {
      if (action === 'confirm') {
        instance.confirmButtonText = 'Loading...'
        instance.confirmButtonLoading = true
        try {
          await handleMinersRemove.mutateAsync({ containerID, minerIDs })
        } catch {
          // Errors should already be processed on the onError callback from deleteFn
        }
      }
      done()
    },
  }).catch(() => {
    // ElMessageBox throws an Error on cancel click (in this case, do nothing).
  })
}

const updating = computed<boolean>(() => !!minerCount.value && isFetching.value)
const miners = computed<IContainerMiner[]>(() => data.value?.data ?? [])
const selectedMiners = ref<IContainerMiner[]>([])
const showMinerOptionButtons = computed<boolean>(() => selectedMiners.value?.length > 0)

const tableEmptyText = computed(() => {
  if (filters.fragment && isFetching.value) return 'Searching...'
  if (isFetching.value) return 'Loading...'
  return 'No results.'
})

const paginationPositionText = computed(() => {
  const endIndex = Math.min(filters.page * filters.per_page, data.value!.total_records)
  const startIndex = Math.min((filters.page - 1) * filters.per_page + 1, data.value!.total_records)
  return `Showing ${startIndex} to ${endIndex} of ${data.value!.total_records}`
})

const debouncedSearch = useDebounceFn((query: string) => {
  filters.fragment = query.trim() || undefined
}, 500)

const handleSelectionChange = (miners: IContainerMiner[]) => {
  selectedMiners.value = miners
}

const handleMinerRowClick = (miner: any) => {
  router.push({ name: 'rc-miner-repairs', params: { minerId: miner.id } })
}

const moveMiners = async (originContainerID: number, destinationContainerID: number, miners: IContainerMiner[]) => {
  try {
    await handleMinersMove.mutateAsync({
      origin: originContainerID,
      destination: destinationContainerID,
      minerIDs: miners.map((m: IContainerMiner) => m.id),
    })
  } catch {
    // Ignore, handled in mutateAsync
  } finally {
    isMoveContainerDialogOpen.value = false
  }
}
</script>

<template>
  <el-card v-loading="loading || isPending">
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button
        :icon="() => h(Icon, { icon: 'material-symbols:refresh-rounded', height: 16, inline: true })"
        @click="refetch()"
        >Retry</el-button
      >
    </rl-error-result>
    <div v-else class="flex flex-col gap-6">
      <header class="inline-flex gap-2 items-center justify-between">
        <div class="inline-flex gap-2 items-center">
          <h4>Miners</h4>
          <el-tag v-if="minerCount" round size="small" type="info" effect="dark">{{ minerCount }}</el-tag>
          <p v-if="props.container.expected_miners" class="text-tertiary font-500">
            {{ props.container.expected_miners }} expected
          </p>
        </div>
        <div class="miner-options">
          <el-button v-if="showMinerOptionButtons" @click="isMoveContainerDialogOpen = true">Move</el-button>
          <el-button
            v-if="showMinerOptionButtons"
            @click="
              RemoveMinersConfirmationModal(
                'Remove Miners',
                'Are you sure you want to remove the chosen miners from the container?',
                props.container.id,
                selectedMiners.map((m: IContainerMiner) => m.id)
              )
            "
            >Remove</el-button
          >
          <el-input
            v-model="searchQuery"
            class="w-[160px]"
            placeholder="Search"
            :disabled="isPending || isFetching"
            clearable
            @update:model-value="debouncedSearch"
          >
            <template #prefix><Icon icon="material-symbols:search-rounded" :inline="true" height="16" /></template>
          </el-input>
        </div>
      </header>
      <template v-if="(minerCount && minerCount > 0) || isFetching">
        <el-table
          v-loading="updating"
          :data="miners"
          show-overflow-tooltip
          :empty-text="tableEmptyText"
          row-class-name="container-miners-table-row"
          @selection-change="handleSelectionChange"
          @row-click="handleMinerRowClick"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column label="Model" prop="model" :min-width="216" />
          <el-table-column
            label="MAC Address"
            prop="mac_address"
            :min-width="140"
            :formatter="optionalValueFallbackFormatter"
          />
          <el-table-column label="Miner SN" prop="sn" :min-width="160" />
          <el-table-column label="PSU SN" prop="psu_sn" :min-width="160" :formatter="optionalValueFallbackFormatter" />
        </el-table>
        <footer>
          <el-pagination
            v-if="data"
            v-model:current-page="filters.page"
            v-model:page-size="filters.per_page"
            :page-sizes="DEFAULT_PAGE_SIZES"
            background
            layout="slot, sizes, prev, pager, next"
            :total="data.total_records ?? 0"
            :disabled="isFetching"
          >
            {{ paginationPositionText }}
          </el-pagination>
        </footer>
      </template>
      <RlEmptyDataset v-else-if="minerCount === 0" title="No miners found in this container." />
    </div>
    <MoveContainerMinersDialog
      v-model="isMoveContainerDialogOpen"
      :miners="selectedMiners"
      :origin-container="props.container"
      @move-miners="moveMiners"
    ></MoveContainerMinersDialog>
  </el-card>
</template>

<style scoped lang="scss">
.el-table {
  /* Global styles should be refactored at some point to get rid of these !important's */
  --el-table-border: none !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-bg-color: transparent;
  --el-table-tr-bg-color: transparent;
}

.el-button {
  margin: 0;
}

.miner-options {
  display: flex;
  gap: var(--spacing-spacing-sm);
}

:deep() {
  .el-table {
    &__inner-wrapper {
      &::before {
        height: 0;
      }
    }
  }

  .el-pagination {
    display: flex;
    justify-content: flex-end;

    &__sizes {
      display: flex;
      justify-content: flex-start;
      flex: 1;
    }
  }

  .container-miners-table-row {
    cursor: pointer;
  }
}
</style>
